import * as Sentry from '@sentry/angular';

// Shared with the inline guard in index.html so the two coordinate.
const GUARD_KEY = 'chunk-reload-at';
// Must exceed CloudFront's s-maxage on index.html (60s) so we don't loop while
// the edge is still serving a stale index.html.
const GUARD_WINDOW_MS = 90_000;
// Cache-busting query param; the same name the previous recovery code used.
// It also carries the reload time, which is the guard when storage is blocked.
const BUST_PARAM = 'v';

// Captured when this module is first evaluated (from main.ts, before
// bootstrap): the URL the tab was opened with.
const ENTRY_URL = new URL(window.location.href);

// Covers both the webpack-era ChunkLoadError shape and the native dynamic
// import() failures thrown by the esbuild-based @angular/build pipeline:
//   - Chrome/Edge: 'Failed to fetch dynamically imported module'
//   - Firefox:     'error loading dynamically imported module'
//   - Safari:      'Importing a module script failed' (network failure), and
//                  "'text/html' is not a valid JavaScript MIME type" when the
//                  missing chunk comes back as index.html (CloudFront 404 -> 200)
const CHUNK_ERROR_REGEX =
  /chunk.?load.?error|loading (css )?chunk \d+|dynamically imported module|importing a module script|is not a valid javascript mime type/i;
const URL_REGEX = /https?:\/\/[^\s'"()]+/gi;

export function isChunkLoadError(err: unknown): boolean {
  if (err == null) return false;
  const anyErr = err as { name?: unknown; message?: unknown };
  const haystack = `${String(anyErr.name ?? '')} ${String(anyErr.message ?? '')} ${String(err)}`;
  if (!CHUNK_ERROR_REGEX.test(haystack)) return false;
  // A third-party script's own lazy chunk failing (Stripe, Square, seats.io,
  // GTM, ...) is not ours to recover from: reloading wouldn't fix it and would
  // throw away the user's page. Only a URL on our origin, or no URL at all
  // (e.g. Safari's MIME message), counts.
  const urls = haystack.match(URL_REGEX) ?? [];
  return urls.length === 0 || urls.some(isOwnUrl);
}

function isOwnUrl(u: string): boolean {
  try {
    return new URL(u).origin === window.location.origin;
  } catch {
    return false;
  }
}

let preserveEntryQuery = false;
let allowUnroutedRecovery: () => boolean = () => true;
let lastRecovery: 'reload' | 'suppressed' | 'blocked' | undefined;

/**
 * App-specific policy (MP-5600).
 * - preserveEntryQuery: when a reload targets a URL without the tab's entry
 *   query params, carry them over (the embed reads smtxorigin, iFrameId,
 *   hideScroll, ... only from the URL it booted with).
 * - allowUnroutedRecovery: consulted for chunk errors that did not come from a
 *   router navigation (window error / unhandledrejection). Return false to
 *   only report them, e.g. while a purchase is in progress.
 */
export function configureChunkRecovery(options: {
  preserveEntryQuery?: boolean;
  allowUnroutedRecovery?: () => boolean;
}): void {
  preserveEntryQuery = options.preserveEntryQuery ?? preserveEntryQuery;
  allowUnroutedRecovery = options.allowUnroutedRecovery ?? allowUnroutedRecovery;
}

/**
 * `href` (default: the current URL) with a fresh cache-busting param; route,
 * query and hash kept. With preserveEntryQuery, entry params the target lacks
 * are added back (the whole entry query, in order, if the target has none).
 */
export function cacheBustedUrl(href: string = window.location.href): string {
  const url = new URL(href, window.location.origin);
  if (preserveEntryQuery && ENTRY_URL.search) {
    if (!url.search) {
      url.search = ENTRY_URL.search;
    } else {
      ENTRY_URL.searchParams.forEach((value, key) => {
        if (!url.searchParams.has(key)) url.searchParams.append(key, value);
      });
    }
  }
  url.searchParams.set(BUST_PARAM, String(Date.now()));
  return url.toString();
}

function readGuard(): number | null {
  try {
    return Number(sessionStorage.getItem(GUARD_KEY) ?? 0);
  } catch {
    return null;
  }
}

function armGuard(): boolean {
  try {
    sessionStorage.setItem(GUARD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

/**
 * Claims the shared reload slot (at most one automatic reload per 90s per tab,
 * shared by chunk recovery, forced updates and update-on-navigation). Returns
 * false when a reload already happened inside the window. Without usable
 * storage (all cookies blocked in a cross-site iframe, or storage full so the
 * write throws), the cache-busting param on the URL this page load started
 * with, which holds the last reload time, is the guard. It is read from
 * ENTRY_URL, not the current URL: the app strips `v` from the address bar on
 * RouteConfigLoadEnd, which can happen before a later chunk fails, and the
 * guard would then never hold (a reload loop).
 */
export function claimReloadSlot(entryUrl: URL = ENTRY_URL): boolean {
  const last = readGuard();
  if (last !== null) {
    if (last > 0 && Date.now() - last < GUARD_WINDOW_MS) return false;
    if (armGuard()) return true;
  }
  const bustedAt = Number(entryUrl.searchParams.get(BUST_PARAM)) || 0;
  return !(bustedAt > 0 && Date.now() - bustedAt < GUARD_WINDOW_MS);
}

let appReloading = false;

/** True once the app has started a page load itself, so unload handlers know the user isn't leaving (keep the cart). */
export function isAppReload(): boolean {
  return appReloading;
}

export function appReload(url: string, replace = true): void {
  appReloading = true;
  if (replace) window.location.replace(url);
  else window.location.assign(url);
}

/** For Sentry's beforeSend: tags chunk-load events whichever handler captured them. */
export function tagChunkLoadEvent<E extends { tags?: { [key: string]: unknown } }>(
  event: E,
  originalException: unknown
): E {
  if (isChunkLoadError(originalException)) {
    event.tags = { ...event.tags, chunk_load_error: 'true', chunk_recovery: lastRecovery ?? 'none' };
  }
  return event;
}

/**
 * One reload per 90s window, so a tab running a stale build picks up the new
 * index.html. Never clears cookies or storage.
 * Pass the router's target URL for a failed navigation (NavigationError.url)
 * so the user lands on the page they were going to; defaults to the current
 * URL. `routed` is false for errors caught by the window handlers, which the
 * app may choose only to report (allowUnroutedRecovery).
 * Returns false when no reload happens.
 */
export function reloadForChunkError(err?: unknown, targetUrl?: string, routed = true): boolean {
  // A failed navigation also rejects the router's promise, which reaches the
  // window handler right after the NavigationError: one recovery is enough.
  if (!routed && lastRecovery === 'reload') return false;
  const last = readGuard();
  if (!routed && !allowUnroutedRecovery()) {
    lastRecovery = 'blocked';
  } else {
    lastRecovery = claimReloadSlot() ? 'reload' : 'suppressed';
  }

  Sentry.captureException(err ?? new Error('Chunk load error'), {
    tags: { chunk_load_error: 'true', chunk_recovery: lastRecovery },
    extra: { url: window.location.href, targetUrl, lastRecoveryAt: last, routed },
  });
  if (lastRecovery !== 'reload') return false;

  const target = cacheBustedUrl(targetUrl ?? window.location.href);
  // Give Sentry a moment to send the event before the page unloads.
  void Sentry.flush(1500).finally(() => appReload(target));
  return true;
}

/**
 * Installs the window-level chunk error handlers and removes the inline
 * index.html guard, which is only needed until the entry bundles have run.
 * Call from main.ts before Sentry.init, so these run before Sentry's own
 * global handlers.
 */
export function installChunkErrorHandlers(): void {
  const w = window as Window & { __chunkGuard?: EventListener };
  if (w.__chunkGuard) {
    window.removeEventListener('error', w.__chunkGuard, true);
    delete w.__chunkGuard;
  }
  window.addEventListener('error', e => {
    if (isChunkLoadError(e.error ?? e.message)) reloadForChunkError(e.error ?? e.message, undefined, false);
  });
  window.addEventListener('unhandledrejection', e => {
    if (isChunkLoadError(e.reason)) reloadForChunkError(e.reason, undefined, false);
  });
}
